import React, {FormEvent, useEffect, useMemo, useRef, useState} from "react";
import {
    Alert,
    Box,
    Checkbox,
    Container,
    FormControlLabel,
    Paper,
    TextField,
    Typography,
} from "@mui/material";
import {AddressElement, Elements, PaymentElement, useElements, useStripe} from "@stripe/react-stripe-js";
import type {StripeElementsOptions} from "@stripe/stripe-js";
import {useFilingStripe} from './components/public-filing-gate';
import {useNavigate, useSearchParams} from "react-router-dom";
import ResponsiveAppBar from "./components/responsive-app-bar";
import Footer from "./components/home/Footer";
import Loading from "./components/loading";
import Spacer from "./components/Spacer";
import {DarkButton} from "./components/buttons";
import {determineLocation} from "./assets/determineLocation";
import {main} from "./assets/colors";
import {recordPaymentAuthorization} from "./assets/paymentAuthorization";
import {observer} from 'mobx-react';
import {reaction} from 'mobx';
import RenewalStore from './stores/renewal-store';
import {CompanyDetailsEditor} from './components/company-details-editor';
import {prepareCompanyReview} from './assets/company-review';
import {SignatureDialog} from './components/signature-dialog';
import {ReinstatementSignatures} from './assets/reinstatement-signature';
import editButtonAction from './ui/renewal/renewal-edit-button-action';
import {FilingFeeSummary} from './components/filing-fee-summary';
import {recordPageEvent} from './assets/page-visit';
import {FortressDivider} from './components/fortress-divider';
import {paymentAppearance} from './assets/payment-appearance';
import {PaymentDetailsHeading} from './components/payment-details-heading';
import {DissolutionForm} from './components/dissolution-form';
import {DissolutionApplication, DissolutionDateBounds, dissolutionApplicationError, dissolutionConsent, emptyDissolutionApplication} from './assets/dissolution-application';
import {FilingSignature} from './assets/renewal-signature';
import {OutstandingReviewIssue, outstandingReviewIssues, ReviewIssue, reviewIssues} from './assets/review-issues';
import {ReviewIssuesAlert} from './components/review-issues-alert';
import {PageErrorBoundary} from './components/page-error-boundary';

type FilingService = "reinstatement" | "dissolution";

interface FilingQuote {
    service: FilingService;
    label: string;
    stateFee: number;
    stateFeeOutput: string;
    serviceFee: number;
    serviceFeeOutput: string;
    total: number;
    totalOutput: string;
    delinquentYears: number[];
    baseStateFeeOutput: string;
    baseServiceFeeOutput: string;
    certifiedCopyFee: number;
    certifiedCopyTotalOutput: string;
    certificateFee: number;
    certificateTotalOutput: string;
}

interface FilingOption {
    service: FilingService;
    label: string;
    available: boolean;
    unavailableReason: string | null;
    quote: FilingQuote | null;
}

interface FilingOptionsResponse {
    company: {
        id: string;
        name: string;
        filingType: string;
        fein: string | null;
        principalAddress: CompanyAddress | null;
        mailingAddress: CompanyAddress | null;
        registeredAgent: CompanyOfficer | null;
        officers: CompanyOfficer[];
    };
    state: {
        lifecycleStatus: string;
        stateStatus: string;
        lastEvent: string;
        delinquentYears: number[];
    };
    options: FilingOption[];
    reviewRestrictions: {generalPartnersAddressOnly: boolean; registeredAgentLocked: boolean};
    /** Problems the server found in the stored company data (reinstatement only; older APIs leave it out). */
    reviewIssues?: ReviewIssue[];
    dissolution?: {dateBounds: DissolutionDateBounds; baseQuote: FilingQuote; certifiedCopyQuote: FilingQuote};
}

interface CompanyAddress {
    street1: string | null;
    street2: string | null;
    city: string | null;
    state: string | null;
    zipCode: string | null;
    country?: string | null;
}

interface CompanyOfficer {
    firstName: string | null;
    lastName: string | null;
    title: string | null;
    address: string | null;
    city: string | null;
    state: string | null;
    zipCode: string | null;
}




const Filing = observer(({service}: {service: FilingService}) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [documentNumber, setDocumentNumber] = useState(searchParams.get("d") || "");
    const [data, setData] = useState<FilingOptionsResponse | null>(null);
    const [application, setApplication] = useState<DissolutionApplication>(() => emptyDissolutionApplication());
    const [dissolutionReview, setDissolutionReview] = useState<DissolutionApplication | null>(null);
    const [clientSecret, setClientSecret] = useState("");
    const [authorizationText, setAuthorizationText] = useState("");
    const [authorizationVersion, setAuthorizationVersion] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [signatureOpen, setSignatureOpen] = useState(false);
    const [initialReview, setInitialReview] = useState<any>(null);
    const preparing = useRef(false);
    const issuesAlert = useRef<HTMLDivElement>(null);
    const requestSequence = useRef(0);

    const selectedOption = useMemo(
        () => data?.options.find(option => option.service === service) || null,
        [data, service],
    );
    const token = searchParams.get("d") || "";
    const isDissolution = service === "dissolution";
    const displayQuote = isDissolution && selectedOption?.available && !clientSecret && data?.dissolution
        ? (application.certifiedCopy ? data.dissolution.certifiedCopyQuote : data.dissolution.baseQuote)
        : selectedOption?.quote;

    useEffect(() => {
        const controller = new AbortController();
        requestSequence.current++;
        preparing.current = false;
        setData(null);
        setClientSecret("");
        setError("");
        setApplication(emptyDissolutionApplication());
        setDissolutionReview(null);
        setSignatureOpen(false);
        setInitialReview(null);
        setLoading(false);
        if (token) void loadOptions(token, controller.signal);
        return () => {
            controller.abort();
            requestSequence.current++;
        };
    }, [token, service]);

    useEffect(() => service === 'reinstatement' ? reaction(
        () => JSON.stringify(RenewalStore.getUpdatedCompanyInfo),
        () => {
            setClientSecret('');
        },
    ) : undefined, [service]);

    async function loadOptions(value: string, signal: AbortSignal) {
        setLoading(true);
        setError("");
        setData(null);
        setClientSecret("");

        try {
            const response = await fetch(`${determineLocation()}/filing/${service}/options?d=${encodeURIComponent(value)}`, {signal});
            const body = await response.json();
            if (!response.ok) {
                throw new Error(typeof body.message === "string" ? body.message : "Unable to verify this entity.");
            }
            if (signal.aborted) return;
            if (service === 'reinstatement') {
                const review = prepareCompanyReview(body.message.company,
                    {keepRegisteredAgent: !!body.message.reviewRestrictions?.registeredAgentLocked});
                RenewalStore.setCompanyInfo(body.message.company);
                RenewalStore.setUpdatedCompanyInfo(review);
                setInitialReview(JSON.parse(JSON.stringify(review)));
            } else {
                setApplication(emptyDissolutionApplication(body.message.dissolution?.dateBounds.min));
            }
            setData(body.message);
        } catch (reason) {
            if (!signal.aborted) setError(reason instanceof Error ? reason.message : "Unable to verify this entity.");
        } finally {
            if (!signal.aborted) setLoading(false);
        }
    }

    /** The server's review issues the visitor has not fixed yet, checked against the current edits. */
    const currentReviewIssues = (): OutstandingReviewIssue[] => !isDissolution && data && initialReview
        ? outstandingReviewIssues(reviewIssues(data.reviewIssues), initialReview, RenewalStore.getUpdatedCompanyInfo, data.reviewRestrictions)
        : [];
    const openIssues = currentReviewIssues();

    function fixReviewIssue(issue: OutstandingReviewIssue) {
        const officer = /^officers\.(\d+)$/.exec(issue.item);
        if (issue.item === 'fein') void editButtonAction({field: 'ein'});
        else if (issue.item === 'principalAddress') void editButtonAction({field: 'principal'});
        else if (issue.item === 'mailingAddress') void editButtonAction({field: 'mailing'});
        else if (issue.item === 'registeredAgent') void editButtonAction({field: 'registered_agent', selectAgentType: true});
        else if (issue.item === 'officers') void editButtonAction({field: 'officer', index: RenewalStore.getUpdatedCompanyInfo.officers.length});
        else if (officer) void editButtonAction({field: 'officer', index: Number(officer[1]),
            addressOnly: data?.reviewRestrictions?.generalPartnersAddressOnly});
    }

    /** Signing waits until every stored-data problem the visitor can fix is fixed. */
    function openSignature() {
        if (currentReviewIssues().some(issue => issue.blocking)) {
            setSignatureOpen(false);
            issuesAlert.current?.scrollIntoView({behavior: 'smooth', block: 'start'});
            return;
        }
        setSignatureOpen(true);
    }

    async function preparePayment(signatures?: ReinstatementSignatures, signedReview?: any, dissolutionSignature?: FilingSignature): Promise<boolean> {
        if (!data || !selectedOption?.available || preparing.current) {
            return false;
        }

        preparing.current = true;
        const sequence = requestSequence.current;
        const reviewedCompany = isDissolution ? null : signedReview;
        if (isDissolution && (!dissolutionSignature || !signedReview || JSON.stringify(signedReview) !== JSON.stringify(application))) {
            preparing.current = false;
            throw new Error('The dissolution details changed. Please review and sign again.');
        }
        if (!isDissolution && (!signatures || !reviewedCompany || JSON.stringify(reviewedCompany) !== JSON.stringify(RenewalStore.getUpdatedCompanyInfo))) {
            preparing.current = false;
            throw new Error('Please review the company information and sign again.');
        }
        if (reviewedCompany && !reviewedCompany.officers.some(officer => officer.remove !== true)) {
            preparing.current = false;
            setError('You must include at least one officer or principal in the reinstatement.');
            if (!isDissolution) throw new Error('You must include at least one officer or principal in the reinstatement.');
            return false;
        }
        setLoading(true);
        setError("");
        try {
            const response = await fetch(`${determineLocation()}/filing/${service}/payment`, {
                method: "POST",
                headers: {"Content-Type": "application/json"},
                body: JSON.stringify({d: token, application: isDissolution
                    ? {...signedReview, documentNumber: data.company.id, signature: dissolutionSignature}
                    : {reviewedCompany, signatures}}),
            });
            const body = await response.json();
            if (sequence !== requestSequence.current) return false;
            if (!response.ok) {
                throw new Error(typeof body.message === "string" ? body.message : "Unable to prepare payment.");
            }
            if (reviewedCompany && JSON.stringify(reviewedCompany) !== JSON.stringify(RenewalStore.getUpdatedCompanyInfo)) {
                throw new Error('Company details changed. Please review and authorize the updated information.');
            }
            setClientSecret(body.message.clientSecret);
            setAuthorizationText(body.message.authorizationText);
            setAuthorizationVersion(body.message.authorizationVersion);
            setData(current => current && ({...current, options: current.options.map(option =>
                option.service === service ? {...option, quote: body.message.quote} : option
            )}));
            return true;
        } catch (reason) {
            if (sequence === requestSequence.current) setError(reason instanceof Error ? reason.message : "Unable to prepare payment.");
            throw reason;
        } finally {
            if (sequence === requestSequence.current) {
                preparing.current = false;
                setLoading(false);
            }
        }
    }

    return <>
        <ResponsiveAppBar showLogin={true}/>
        <Container maxWidth="md" sx={{py: 6}}>
            <Typography variant="h3" align="center" color={main}>
                {isDissolution ? "Dissolve a Florida company" : "Reinstate a Florida company"}
            </Typography>
            <Typography align="center" sx={{mt: 2}}>
                {isDissolution ? "Review and authorize Articles of Dissolution for your company."
                    : "Restore an administratively dissolved or revoked entity to active status."}
            </Typography>

            {isDissolution && !token && <Alert severity="warning" sx={{mt: 3}}>
                A dedicated dissolution link is required. Please contact support to request one for your company.
                Renewal and reinstatement links cannot be used to dissolve a company.
            </Alert>}
            {!isDissolution && !token && <Paper component="form" onSubmit={(event: FormEvent) => {
                event.preventDefault();
                const value = documentNumber.trim();
                if (value) setSearchParams({d: value.length < 15 ? value.toUpperCase() : value});
            }} sx={{p: 3, mt: 3}}>
                <TextField
                    fullWidth
                    label="Florida document number"
                    value={documentNumber}
                    onChange={event => setDocumentNumber(event.target.value)}
                    required
                />
                <DarkButton type="submit" variant="contained" sx={{mt: 2}} disabled={loading}>
                    Check reinstatement eligibility
                </DarkButton>
            </Paper>}

            {loading && <Loading/>}
            {error && !(data && !clientSecret && !isDissolution && selectedOption?.available) && <Alert severity="error" sx={{mt: 3}}>{error}</Alert>}

            {data && !clientSecret && <>
                <Paper sx={{p: 3, mt: 3}}>
                    <Typography variant="h5">{data.company.name}</Typography>
                    <Typography>{data.company.id} · Sunbiz status: {data.state.stateStatus}</Typography>
                    <Typography color="text.secondary">Last event: {data.state.lastEvent || "Not available"}</Typography>
                </Paper>

                {displayQuote ? <Paper variant="outlined" sx={{p: 3, mt: 3}}>
                    <Typography variant="h6">{selectedOption?.label}</Typography>
                    <Typography variant="h5">Total {displayQuote.totalOutput}</Typography>
                    {!!displayQuote.delinquentYears.length && <Typography color="text.secondary">
                        Includes delinquent annual reports: {displayQuote.delinquentYears.join(", ")}
                    </Typography>}
                </Paper> : <Alert severity="warning" sx={{mt: 3}}>
                    {selectedOption?.unavailableReason || "This filing is not available for this company."}
                </Alert>}

                {selectedOption?.available && <>
                    {!isDissolution && <ReviewIssuesAlert ref={issuesAlert} issues={openIssues} onFix={fixReviewIssue}/>}
                    {!isDissolution && <Box component="fieldset" disabled={loading} sx={{border: 0, p: 0, m: 0, mt: 3, minWidth: 0}}>
                        <PageErrorBoundary>
                            <CompanyDetailsEditor companyInfo={data.company} selectAgentType
                                                  generalPartnersAddressOnly={data.reviewRestrictions?.generalPartnersAddressOnly}
                                                  registeredAgentLocked={data.reviewRestrictions?.registeredAgentLocked}/>
                        </PageErrorBoundary>
                    </Box>}
                    {isDissolution ? data.dissolution ? <DissolutionForm key={data.company.id}
                    application={application}
                    setApplication={setApplication}
                    dateBounds={data.dissolution.dateBounds}
                    certifiedCopyPrice={data.dissolution.certifiedCopyQuote.certifiedCopyTotalOutput}
                    onContinue={() => {
                        const message = dissolutionApplicationError(application, data.dissolution!.dateBounds);
                        setError(message ?? '');
                        if (!message) {recordPageEvent('proceed'); setDissolutionReview(JSON.parse(JSON.stringify(application))); setSignatureOpen(true);}
                    }}
                    loading={loading}
                    /> : <Alert severity="error" sx={{mt: 3}}>Dissolution details could not be loaded. Please refresh.</Alert> : <Container maxWidth="sm" sx={{display: 'flex', flexDirection: 'column'}}>
                        {error && <Alert severity="error" sx={{mt: 3}} role="alert">{error}</Alert>}
                        {openIssues.some(issue => issue.blocking) && <Typography variant="body2" color="error" sx={{mt: 3}}>
                            Update the details listed above before signing.
                        </Typography>}
                        <DarkButton onClick={() => {recordPageEvent('proceed'); setError(''); openSignature();}} disabled={loading}
                                    sx={{p: 2, mt: 3}} disableElevation variant="contained">Proceed to payment</DarkButton>
                    </Container>}
                </>}
            </>}

            {clientSecret && data && selectedOption?.quote && <PaymentSection
                company={data.company}
                clientSecret={clientSecret}
                quote={selectedOption.quote}
                authorizationText={authorizationText}
                authorizationVersion={authorizationVersion}
                onBack={() => setClientSecret("")}
            />}
            {isDissolution && data && dissolutionReview && <SignatureDialog open={signatureOpen}
                onClose={() => setSignatureOpen(false)} documentNumber={data.company.id}
                singleSigner={{heading: 'Authorize Dissolution', companyName: data.company.name,
                    name: dissolutionReview.signerName, reviewVersion: JSON.stringify(application), consent: dissolutionConsent,
                    onSign: async signature => {
                        if (!await preparePayment(undefined, dissolutionReview, signature)) throw new Error('The company has changed. Please review and sign again.');
                    },
                }}/>}
            {!isDissolution && data && <SignatureDialog open={signatureOpen} onClose={() => setSignatureOpen(false)}
                officers={RenewalStore.getUpdatedCompanyInfo.officers}
                documentNumber={data.company.id} reinstatement={{
                    registeredAgentLocked: data.reviewRestrictions?.registeredAgentLocked ?? false,
                    onSign: async (signatures, reviewedCompany) => {
                        if (!await preparePayment(signatures, reviewedCompany)) throw new Error('The company has changed. Please review and sign again.');
                    },
                    onUpdateAgent: async () => {
                        const sequence = requestSequence.current;
                        setSignatureOpen(false);
                        await editButtonAction({field: 'registered_agent', selectAgentType: true});
                        if (sequence === requestSequence.current) openSignature();
                    },
                }}/>}
        </Container>
        <Spacer height={100}/>
        <Footer/>
    </>;
});



const PaymentSection = ({company, clientSecret, quote, authorizationText, authorizationVersion, onBack}: {
    company: {name: string; id: string};
    clientSecret: string;
    quote: FilingQuote;
    authorizationText: string;
    authorizationVersion: string;
    onBack: () => void;
}) => {
    const stripePromise = useFilingStripe();
    const [submitting, setSubmitting] = useState(false);
    const options: StripeElementsOptions = {clientSecret, appearance: paymentAppearance};

    useEffect(() => {window.scrollTo(0, 0); recordPageEvent('payment_shown');}, []);

    return <Box sx={{mt: 3}}>
        <PaymentDetailsHeading description={`${quote.label} · ${company.name} (${company.id})`}/>
        <Box sx={{mt: 3, mb: 3}}>
            <FilingFeeSummary total={quote.totalOutput} onExpand={() => recordPageEvent('breakdown')} rows={[
                {label: 'Service fee', amount: quote.baseServiceFeeOutput},
                {label: quote.service === 'reinstatement' ? 'State reinstatement and annual report fees' : 'State dissolution fee', amount: quote.baseStateFeeOutput},
                ...(quote.certificateFee > 0 ? [{label: 'Certificate of Status', amount: quote.certificateTotalOutput}] : []),
                ...(quote.certifiedCopyFee > 0 ? [{label: 'Certified Copy of Articles of Dissolution', amount: quote.certifiedCopyTotalOutput}] : []),
            ]}/>
            {quote.service === 'dissolution' && <Typography variant="body2" color="text.secondary" sx={{mt: 1}}>Certificate of Status included.</Typography>}
        </Box>
        <Elements key={clientSecret} stripe={stripePromise} options={options}>
            <FilingCheckout
                clientSecret={clientSecret}
                authorizationText={authorizationText}
                authorizationVersion={authorizationVersion}
                onSubmitting={setSubmitting}
            />
        </Elements>
        <Container maxWidth="sm" sx={{display: 'flex', flexDirection: 'column'}}>
            <DarkButton type="button" onClick={() => {recordPageEvent('back'); onBack();}} disabled={submitting} sx={{mt: 2}}>Back to company details</DarkButton>
        </Container>
    </Box>;
};

const FilingCheckout = ({clientSecret, authorizationText, authorizationVersion, onSubmitting}: {
    clientSecret: string;
    authorizationText: string;
    authorizationVersion: string;
    onSubmitting: (value: boolean) => void;
}) => {
    const stripe = useStripe();
    const elements = useElements();
    const navigate = useNavigate();
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [chargeAccepted, setChargeAccepted] = useState(false);
    const inFlight = useRef(false);

    async function submit(event: FormEvent) {
        event.preventDefault();
        if (!stripe || !elements || !chargeAccepted || inFlight.current) return;

        inFlight.current = true;
        recordPageEvent('submit');
        setSubmitting(true);
        onSubmitting(true);
        setError("");
        try {
            await recordPaymentAuthorization(clientSecret, authorizationText, authorizationVersion);
            const result = await stripe.confirmPayment({
                elements,
                confirmParams: {return_url: `${window.location.origin}/complete`},
            });
            if (result.error) throw new Error(result.error.message || 'Payment could not be completed.');
            navigate('/complete');
        } catch (reason) {
            recordPageEvent('payment_error');
            setError(reason instanceof Error ? reason.message : "Payment could not be completed. Please try again.");
            inFlight.current = false;
            setSubmitting(false);
            onSubmitting(false);
        }
    }

    return <form onSubmit={submit}>
        <FortressDivider textAlign="left" sx={{m: 2}}>Billing Information</FortressDivider>
        <PaymentElement options={{fields: {billingDetails: {name: 'auto', email: 'auto', address: 'auto'}}}}/>
        <FortressDivider textAlign="left" sx={{m: 2}}>Billing Address</FortressDivider>
        <AddressElement options={{mode: "billing"}}/>
        <FormControlLabel
            control={<Checkbox checked={chargeAccepted} disabled={submitting} onChange={event => {if (event.target.checked) recordPageEvent('charge_accepted'); setChargeAccepted(event.target.checked);}}/>}
            label={authorizationText}
            sx={{mt: 3}}
        />
        {error && <Alert severity="error" sx={{mt: 2}}>{error}</Alert>}
        <Container maxWidth="sm" sx={{display: 'flex', flexDirection: 'column'}}>
            <DarkButton type="submit" variant="contained" disableElevation disabled={!stripe || !chargeAccepted || submitting} sx={{p: 2, mt: 3}}>
                {submitting ? 'Submitting...' : 'Submit Payment'}
            </DarkButton>
        </Container>
    </form>;
};

export default Filing;
